import { useEffect, useReducer, useState } from 'react';

import {
    styled,
    Box,
    Card,
    CardHeader,
    CardContent,
    Typography,
    CircularProgress,
    IconButton,
    Switch,
    FormControlLabel,
    TextField,
    InputAdornment,
    Tooltip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Alert
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';

import { ActionButton } from 'components/ui/buttons';
import { DeleteButton } from './ui/buttons';
import { SubmitButton } from './ui/buttons';
import { CancelButton } from './ui/buttons';
import fetchExpresso from 'utility/fetchExpresso';
import { useAuth } from 'context/auth';
import { useNotification } from 'context/notification';
import { useTextInput, useToggle } from 'hooks/forms';
import { validateRobinUrlOrId } from 'utility/validation';

const ROBIN_WEBHOOK_ID = 1;

const DEV_PUBLIC_API_URL = 'https://dev-api.frontdoorplus.com';

/** URL Robin calls to list events (may differ from portal API base in local dev). */
function getRobinEventsApiBaseUrl() {
    if (process.env.REACT_APP_ENV === 'development') {
        return DEV_PUBLIC_API_URL;
    }
    return String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
}


const DEFAULT_FORM_FLOW_CONFIG = {
    formName: 'Purchase Order',
    flowType: 'payment_then_approval',
    steps: [
        { sourceField: 'supplier', condition: 'required', target: 'line_items' },
        { sourceField: 'line_items', condition: 'required', target: 'confirm' },
        { sourceField: 'confirm', condition: 'accepted', target: 'payment_details' },
        { sourceField: 'payment_method', condition: 'required', target: 'approval' },
        { sourceField: 'approver', condition: 'required', target: 'submit' },
    ],
};

function generateSecret() {
    return crypto.randomUUID();
}

const initialState = {
    status: 'loading',
    webhooks: [],
    createModalOpen: false,
    editWebhook: null,
    secretModal: null,
};

function reducer(state, action) {
    switch (action.type) {
        case 'loadSuccess':
            return { ...state, status: 'success', webhooks: action.webhooks };
        case 'loadError':
            return { ...state, status: 'error' };
        case 'openCreate':
            return { ...state, createModalOpen: true };
        case 'closeCreate':
            return { ...state, createModalOpen: false };
        case 'openEdit':
            return { ...state, editWebhook: action.webhook };
        case 'closeEdit':
            return { ...state, editWebhook: null };
        case 'openSecret':
            return { ...state, createModalOpen: false, secretModal: action.secret };
        case 'closeSecret':
            return { ...state, secretModal: null };
        case 'reload':
            return { ...state, status: 'loading' };
        case 'refresh':
            return {
                ...state,
                createModalOpen: false,
                editWebhook: null,
                secretModal: null,
                status: 'loading',
            };
        default:
            return state;
    }
}

export default function HostWebhooks() {
    const { auth } = useAuth();
    const { notify } = useNotification();
    const [state, dispatch] = useReducer(reducer, initialState);

    const robinEventsFeedUrl = `${getRobinEventsApiBaseUrl()}/public/hosts/${auth.hostId}/events`;

    useEffect(() => {
        if (state.status !== 'loading') return;

        let cancelled = false;

        fetchExpresso(`/apiv2/hosts/${auth.hostId}/webhooks`)
            .then(async (res) => {
                if (cancelled) return;
                if (res.status === 200) {
                    const webhooks = await res.json();
                    dispatch({ type: 'loadSuccess', webhooks });
                } else {
                    dispatch({ type: 'loadError' });
                }
            })
            .catch(() => {
                if (!cancelled) dispatch({ type: 'loadError' });
            });

        return () => {
            cancelled = true;
        };
    }, [auth.hostId, state.status]);

    const handleCopyRobinEventsFeedUrl = async () => {
        try {
            await navigator.clipboard.writeText(robinEventsFeedUrl);
            notify.success('Robin link copied to clipboard');
        } catch {
            notify.error('Unable to copy Robin link');
        }
    };

    return (
        <>
            <Card>
                <CardHeader title='Robin AI Event Support' />
                <CardContent>
                    <Typography paragraph>
                        Give your ticket buyers instant event support via SMS. Robin automatically
                        reaches out to attendees after purchase and helps answer common event
                        questions before and during your event.
                    </Typography>

                    <Box my={4}>
                        <Alert severity="info">
                            To get started, visit{' '}
                            <Typography
                                component="a"
                                href="https://app.robin.guide"
                                target="_blank"
                                rel="noreferrer"
                                sx={{
                                    color: 'inherit',
                                    textDecoration: 'underline',
                                }}
                            >
                                Robin.guide
                            </Typography>
                            {' '}to create an account. Then enable the FrontDoor+ Skill.
                        </Alert>
                    </Box>

                    <SubsectionTitle>Automated Attendee Support</SubsectionTitle>

                    <Typography component="div" paragraph>
                        We&apos;ll notify your Robin when a customer buys a ticket so it can message
                        them to offer support. Get your Robin Location from your{' '}
                        <Typography
                            component="a"
                            href="https://app.robin.guide"
                            target="_blank"
                            rel="noreferrer"
                            sx={{
                                color: 'inherit',
                                textDecoration: 'underline',
                            }}
                        >
                            Robin Dashboard
                        </Typography>{' '}
                        &gt; Skills &gt; FrontDoor+ &gt; Automated Attendee Support. Then click
                        connect robin below.
                    </Typography>

                    <Box mb={2}>
                        {state.status === 'loading' && (
                            <Box display="flex" justifyContent="center" alignItems="center" height={80}>
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {state.status === 'error' && (
                            <Typography color="error">
                                Something went wrong. Please refresh the page.
                            </Typography>
                        )}

                        {state.status === 'success' &&
                            state.webhooks.map((webhook) => (
                                <RobinListRow key={webhook.host_webhook_id}>
                                    <Typography
                                        fontWeight="bold"
                                        color={webhook.status === 1 ? 'text.primary' : 'text.disabled'}
                                    >
                                        {webhook.status === 1 ? webhook.webhook_label : `${webhook.webhook_label} (disabled)`}
                                    </Typography>
                                    <Button
                                        color="secondary"
                                        onClick={() => dispatch({ type: 'openEdit', webhook })}
                                    >
                                        Manage Robin
                                    </Button>
                                </RobinListRow>
                            ))}
                    </Box>

                    <ActionButton onClick={() => dispatch({ type: 'openCreate' })}>
                        Connect Robin
                    </ActionButton>

                    <SubsectionTitle sx={{ mt: 4 }}>Event Knowledge</SubsectionTitle>
                    <Typography paragraph>
                        Your Robin can use this link to help answer questions. Copy the link below,
                        then in your Robin dashboard, go to Skills &gt; FrontDoor+ &gt; Event
                        Knowledge, and paste it there.
                    </Typography>

                    <Box sx={{ pt: 1 }}>
                        <TextField
                            value={robinEventsFeedUrl}
                            fullWidth
                            variant="outlined"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Copy Robin link to clipboard">
                                            <IconButton
                                                aria-label="copy robin link"
                                                onClick={handleCopyRobinEventsFeedUrl}
                                                edge="end"
                                            >
                                                <ContentCopyOutlinedIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {state.createModalOpen && (
                <CreateRobinDialog
                    open
                    hostId={auth.hostId}
                    onClose={() => dispatch({ type: 'closeCreate' })}
                    onCreated={(secret) => {
                        notify.success('Robin added');
                        dispatch({ type: 'openSecret', secret });
                        dispatch({ type: 'reload' });
                    }}
                />
            )}

            {state.editWebhook && (
                <EditRobinDialog
                    open
                    hostId={auth.hostId}
                    webhook={state.editWebhook}
                    onClose={() => dispatch({ type: 'closeEdit' })}
                    onSaved={() => {
                        notify.success('Robin updated');
                        dispatch({ type: 'refresh' });
                    }}
                    onDeleted={() => {
                        notify.success('Robin deleted');
                        dispatch({ type: 'refresh' });
                    }}
                />
            )}

            <SecretDialog
                open={state.secretModal !== null}
                secret={state.secretModal || ''}
                onClose={() => dispatch({ type: 'closeSecret' })}
            />
        </>
    );
}

function CreateRobinDialog(props) {
    const { open, hostId, onClose, onCreated } = props;
    const { notify } = useNotification();
    const [submitting, setSubmitting] = useState(false);

    const label = useTextInput({ defaultValue: '', maxLength: 128 });
    const urlOrId = useTextInput({ defaultValue: '', maxLength: 500 });

    const handleCreate = async () => {
        let formError = false;

        if (!label.value.trim()) {
            label.setError('Label is required');
            formError = true;
        }

        if (!urlOrId.value.trim()) {
            urlOrId.setError('Robin URL or Agent ID is required');
            formError = true;
        } else if (!validateRobinUrlOrId(urlOrId.value)) {
            urlOrId.setError(
                'Enter a valid URL (https://...) or a Robin agent ID without slashes',
            );
            formError = true;
        }

        if (formError) return;

        const secret = generateSecret();

        setSubmitting(true);

        try {
            const res = await fetchExpresso(`/apiv2/hosts/${hostId}/webhooks`, {
                method: 'POST',
                body: {
                    webhookId: ROBIN_WEBHOOK_ID,
                    label: label.value.trim(),
                    urlOrId: urlOrId.value.trim(),
                    secret,
                    config: DEFAULT_FORM_FLOW_CONFIG,
                },
            });

            if (res.status === 200) {
                onCreated(secret);
            } else {
                notify.error('Unable to add Robin');
            }
        } catch {
            notify.error('Unable to add Robin');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Connect Robin</DialogTitle>
            <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
                <FieldRow sx={{ mt: 0.5 }}>
                    <TextField
                        {...label.formProps}
                        label="Label"
                        placeholder="e.g. Baron von Beak"
                        helperText="Enter the name of your Robin"
                        fullWidth
                        variant="outlined"
                        autoFocus
                    />
                </FieldRow>
                <FieldRow>
                    <TextField
                        {...urlOrId.formProps}
                        label="Delivery URL or Agent ID"
                        helperText="Enter your Robin delivery URL (https://...) or agent ID from your Robin dashboard"
                        fullWidth
                        variant="outlined"
                    />
                </FieldRow>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <CancelButton onClick={onClose}>
                    Cancel
                </CancelButton>
                <SubmitButton onClick={handleCreate} disabled={submitting}>
                    {submitting ? 'Connecting...' : 'Connect Robin'}
                </SubmitButton>
            </DialogActions>
        </Dialog>
    );
}

function EditRobinDialog(props) {
    const { open, hostId, webhook, onClose, onSaved, onDeleted } = props;
    const { notify } = useNotification();
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    const label = useTextInput({
        defaultValue: webhook.webhook_label || '',
        maxLength: 128,
    });

    const urlOrId = useTextInput({
        defaultValue: webhook.url || '',
        maxLength: 500,
    });

    const secret = useTextInput({
        defaultValue: webhook.secret || '',
        maxLength: 255,
    });

    const enabled = useToggle({
        defaultValue: webhook.status === 1,
    });

    const handleCopySecret = async () => {
        const value = secret.value.trim();
        if (!value) return;

        try {
            await navigator.clipboard.writeText(value);
            notify.success('Secret copied to clipboard');
        } catch {
            notify.error('Unable to copy secret');
        }
    };

    const handleSave = async () => {
        let formError = false;

        if (!label.value.trim()) {
            label.setError('Label is required');
            formError = true;
        }

        if (!urlOrId.value.trim()) {
            urlOrId.setError('Robin URL or Agent ID is required');
            formError = true;
        } else if (!validateRobinUrlOrId(urlOrId.value)) {
            urlOrId.setError(
                'Enter a valid URL (https://...) or a Robin agent ID without slashes',
            );
            formError = true;
        }

        if (!secret.value.trim()) {
            secret.setError('Secret is required');
            formError = true;
        }

        if (formError) return;

        setSubmitting(true);

        try {
            const res = await fetchExpresso(
                `/apiv2/hosts/${hostId}/webhooks/${webhook.host_webhook_id}`,
                {
                    method: 'PUT',
                    body: {
                        label: label.value.trim(),
                        urlOrId: urlOrId.value.trim(),
                        secret: secret.value.trim(),
                        status: enabled.checked ? 1 : 0,
                    },
                },
            );

            if (res.status === 200) {
                onSaved();
            } else {
                notify.error('Unable to save Robin');
            }
        } catch {
            notify.error('Unable to save Robin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        if (submitting) return;

        setSubmitting(true);

        try {
            const res = await fetchExpresso(
                `/apiv2/hosts/${hostId}/webhooks/${webhook.host_webhook_id}`,
                {
                    method: 'DELETE',
                },
            );

            if (res.status === 200) {
                onDeleted();
            } else {
                notify.error('Unable to delete Robin');
            }
        } catch {
            notify.error('Unable to delete Robin');
        } finally {
            setSubmitting(false);
            setDeleteConfirmOpen(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
                <FieldRow sx={{ mt: 0.5 }}>
                    <TextField
                        {...label.formProps}
                        label="Label"
                        helperText="Enter the name of your Robin"
                        fullWidth
                        variant="outlined"
                        autoFocus
                    />
                </FieldRow>
                <FieldRow>
                    <TextField
                        {...urlOrId.formProps}
                        label="Delivery URL or Agent ID"
                        helperText="Enter your Robin delivery URL (https://...) or agent ID from your Robin dashboard"
                        fullWidth
                        variant="outlined"
                    />
                </FieldRow>
                <FieldRow>
                    <TextField
                        {...secret.formProps}
                        label="Secret"
                        helperText="Copy this secret to Robin so they can verify messages from us."
                        fullWidth
                        variant="outlined"
                        InputProps={{
                            readOnly: true,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title="Copy secret to clipboard">
                                        <IconButton
                                            aria-label="copy secret"
                                            onClick={handleCopySecret}
                                            edge="end"
                                        >
                                            <ContentCopyOutlinedIcon />
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                        }}
                    />
                </FieldRow>
                <FieldRow>
                    <FormControlLabel
                        control={<Switch {...enabled.formProps} />}
                        label={enabled.checked ? 'Enabled' : 'Disabled'}
                    />
                </FieldRow>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <DeleteButton sx={{ marginRight: 'auto' }} onClick={() => setDeleteConfirmOpen(true)} disabled={submitting}>
                    Delete
                </DeleteButton>
                <CancelButton onClick={onClose} disabled={submitting}>
                    Cancel
                </CancelButton>
                <SubmitButton onClick={handleSave} disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save'}
                </SubmitButton>
            </DialogActions>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => {
                    if (!submitting) setDeleteConfirmOpen(false);
                }}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Delete Robin Connection?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This will permanently delete {webhook.webhook_label || 'this Robin'}.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <CancelButton onClick={() => setDeleteConfirmOpen(false)} disabled={submitting}>
                        Cancel
                    </CancelButton>
                    <DeleteButton onClick={handleDelete} disabled={submitting}>
                        {submitting ? 'Deleting...' : 'Delete'}
                    </DeleteButton>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
}

function SecretDialog(props) {
    const { open, secret, onClose } = props;
    const { notify } = useNotification();

    const handleCopySecret = async () => {
        if (!secret) return;

        try {
            await navigator.clipboard.writeText(secret);
            notify.success('Secret copied to clipboard');
        } catch {
            notify.error('Unable to copy secret');
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>FrontDoor+ Secret</DialogTitle>
            <DialogContent sx={{ pt: 3, overflow: 'visible' }}>
                <Typography paragraph>
                    Copy this secret to Robin so they can verify messages from us. You can view this secret anytime visiting this page again and clicking on your Robin connection.
                </Typography>
                <TextField
                    label="FrontDoor+ Secret"
                    value={secret}
                    fullWidth
                    variant="outlined"
                    sx={{ my: 2 }}
                    InputProps={{
                        readOnly: true,
                        endAdornment: (
                            <InputAdornment position="end">
                                <Tooltip title="Copy secret to clipboard">
                                    <IconButton
                                        aria-label="copy secret"
                                        onClick={handleCopySecret}
                                        edge="end"
                                    >
                                        <ContentCopyOutlinedIcon />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ),
                    }}
                />
                <Box component="ul" sx={{ pl: 4 }}>
                    <Typography component="li">
                        Once you copy the secret, go to your FrontDoor+ Skill in Robin
                    </Typography>
                    <Typography component="li">Paste it under FrontDoor+ Secret</Typography>
                    <Typography component="li">Click save to complete the connection</Typography>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <ActionButton onClick={onClose}>Done</ActionButton>
            </DialogActions>
        </Dialog>
    );
}

const FieldRow = styled(Box)({
    marginBottom: 24,
});

const SubsectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
}));

const RobinListRow = styled(Paper)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    border: '1px solid rgba(0, 0, 0, 0.23)',
}));
